import React, { useContext } from "react";

// Context
import { CoreContext } from "../../../context";

// Themes
import { BACKGROUND_VARIANTS } from "../../../config";
import { InformationCircle } from "../../../components/icons/svg/outline";

// Components
import ScopeHoverDetails from "../../planning/dailyDispatch/workScopeSummary/ScopeHoverDetails";
import { Spinner } from "../../../components/ui";

/**
 * ScopeInformation component - displays scope information for a shift. Filters the
 * scope data sent to find the first (and should be only) scope that matches the other
 * shift information sent in (date, job number, job description).
 *
 * Props:
 * - scopes: Array of scope objects, each containing date, job number, and job description.
 * - date: The date to filter scopes by (string in YYYY-MM-DD format).
 * - jobNum: The job number to filter scopes by (string or number).
 * - shiftId: The shift ID (null for newly added shifts).
 *
 * @param {Object} props Component props.
 * @param {Array<Object>} props.scopes Scope objects for the week.
 * @param {Object} props.worker Worker used by {@link ScopeHoverDetails}.
 * @param {string} props.date Date to filter scopes (`YYYY-MM-DD`).
 * @param {string|number} props.jobNum Job number to filter scopes by.
 * @param {number|null|undefined} props.shiftId Shift id; null/undefined means a newly added payout shift.
 * @returns {JSX.Element}
 */
const ScopeInformation = ({ scopes, worker, date, jobNum, shiftId }) => {
  const { scopesList } = useContext(CoreContext);

  // Check if this is a newly added payout shift (id is null)
  const isAddedShift = shiftId === null || shiftId === undefined;

  // If this is an added shift, show gray info icon with a native tooltip
  if (isAddedShift) {
    return (
      <div className="flex items-center justify-center" title="Payout added shift">
        <InformationCircle className="h-4 w-4 text-gray-500" />
      </div>
    );
  }

  if (!scopes || scopes.length === 0) {
    return (
      <div className="group flex items-center justify-center relative">
        <Spinner variant="oval" className="text-gray-500" />
      </div>
    );
  }

  if (scopes === "No scopes found") {
    return (
      <div className="flex items-center justify-center" title="No scopes found for this date.">
        <InformationCircle className="h-4 w-4 text-red-600" />
      </div>
    );
  }

  var mismatchError = false;
  var scopeData = null;

  // Find the scope that matches the date and the job number the worker clocked into
  const workerClockScope = scopes.find(
    (scope) =>
      scope.date === date &&
      scope.job.number === jobNum &&
      scope.worker_assignment.find((wa) => wa.id === worker.id)
  );

  // If we did not find the worker at the scope they clocked into in the dispatch,
  // then we show a message that they clocked into a different job.
  if (!workerClockScope) {
    mismatchError = true;
    scopeData = scopes.find((scope) =>
      scope.worker_assignment.find((wa) => wa.id === worker.id)
    );
  } else {
    scopeData = workerClockScope;
  }

  if (!scopeData) {
    return (
      <div
        className="flex items-center justify-center"
        title="No scopes found for this job on this date."
      >
        <InformationCircle className="h-4 w-4 text-red-600" />
      </div>
    );
  }

  // Determine icon color based on error condition
  const iconColor = mismatchError ? "text-red-600" : "textblack";

  return (
    <div className="group flex items-center justify-center relative">
      <InformationCircle className={`h-4 w-4 ${iconColor}`} />
      {/* Scope Tooltip */}
      <div className="absolute z-10 top-full mt-1 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
        <div className="bg-white rounded shadow-lg">
          {mismatchError ? (
            <>
              <p className="p-1 text-red-800 font-bold rounded-t-md bg-gray-300 text-center">
                Job punch mismatch.
              </p>
              <p className="p-1 text-black font-bold rounded-t-md bg-gray-200 text-center">
                Expected job/scope:
              </p>
            </>
          ) : (
            <p className="p-1 text-green-800 font-bold rounded-t-md bg-gray-200 text-center">
              Job punch matches dispatch
            </p>
          )}
          <p
            className={`font-bold text-lg pl-2 pt-2 text-white ${mismatchError ? "bg-green-800" : BACKGROUND_VARIANTS.dark.bg}`}
          >
            {scopeData.job.number} - {scopeData.scope.title}
          </p>
          <ScopeHoverDetails
            scope={scopeData || {}}
            containerWidth={300}
            className={`${mismatchError ? "" : "rounded-b-md"} ${mismatchError ? "bg-green-800" : BACKGROUND_VARIANTS.dark.bg}`}
          />
          {mismatchError && (
            <>
              <p className="p-1 text-black font-bold bg-gray-200 text-center">
                Job/scope punched:
              </p>
              <p
                className={`font-bold text-lg pl-2 py-2 rounded-b-md text-white ${mismatchError ? "bg-red-800" : BACKGROUND_VARIANTS.dark.bg}`}
              >
                {jobNum} -{" "}
                {scopesList.find((s) => s.id === scopeData.scope.id)?.title ||
                  "No Scope Set"}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScopeInformation;
